import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Filter, Plus, Download, MapPin } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { WeekView } from '../../components/WeekView';
import { adminService } from '../../services/adminService';
import NewOrderDialog from './orders/NewOrderDialog';
import { CustomOrderFormDialog } from '../../components/customOrders/CustomOrderFormDialog';
import { ExportDialog } from '../../components/exports/ExportDialog';
import { toast } from 'sonner';
import { statusConfig } from './orders/ordersConstants';
import { isNonCountingStatus } from '../../lib/orderTotals';
import { OrdersErrorBoundary } from './orders/OrdersErrorBoundary';
import { OrdersTable } from './orders/OrdersTable';
import { BatchUpdateDialog } from './orders/BatchUpdateDialog';
import { OrderDetailDialog } from './orders/OrderDetailDialog';
import { ChangeRequestsSection } from './orders/ChangeRequestsSection';
import { LocationBacklogDialog } from './orders/LocationBacklogDialog';
import { ApprovalDialog } from './overOrdering/ApprovalDialog';
import { useNewOrder } from './orders/useNewOrder';

const AdminOrdersContent = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [exportOpen, setExportOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [startInEdit, setStartInEdit] = useState(false);
  const [customOrderOpen, setCustomOrderOpen] = useState(false);
  const [customOrderEditing, setCustomOrderEditing] = useState(null);
  const [backlogOpen, setBacklogOpen] = useState(false);
  const [approvalOrderId, setApprovalOrderId] = useState(null);

  // Batch selection
  const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [batchStatus, setBatchStatus] = useState('');
  const [weekFilteredOrders, setWeekFilteredOrders] = useState(null);
  const [weekScopeOrders, setWeekScopeOrders] = useState(null);

  const loadData = async () => {
    try {
      const [ordersData, clientsData, productsData] = await Promise.all([
        adminService.getOrders(),
        adminService.getClients(),
        adminService.getProducts(),
      ]);
      setOrders(ordersData);
      setClients(clientsData);
      setProducts(productsData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const newOrder = useNewOrder({ clients, onCreated: loadData });

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      await adminService.updateOrderStatus(orderId, newStatus);
      const updateStatus = o => o.id === orderId ? { ...o, status: newStatus } : o;
      setOrders(prev => prev.map(updateStatus));
      setWeekFilteredOrders(prev => prev ? prev.map(updateStatus) : prev);
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(prev => ({ ...prev, status: newStatus }));
      }
      toast.success(`Order status updated to ${newStatus.replace('_', ' ')}`);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update order status');
    }
  };

  const handleOrderSaved = (updatedOrder) => {
    setSelectedOrder(updatedOrder);
    setOrders(prev => prev.map(o => o.id === updatedOrder.id ? updatedOrder : o));
    setWeekFilteredOrders(prev => prev ? prev.map(o => o.id === updatedOrder.id ? updatedOrder : o) : prev);
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setStartInEdit(false);
    setDetailOpen(true);
  };

  // Deep link from an invoice: /admin/orders?order=<id> opens that order's
  // detail (and therefore its Delivery Proof panel) straight away.
  useEffect(() => {
    const wanted = searchParams.get('order');
    if (!wanted || orders.length === 0) return;
    const match = orders.find((o) => o.id === wanted);
    if (!match) {
      toast.error('That order is no longer in the list');
    } else {
      setSelectedOrder(match);
      setStartInEdit(false);
      setDetailOpen(true);
    }
    const next = new URLSearchParams(searchParams);
    next.delete('order');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, searchParams]);

  const handleEditRow = (order) => {
    if (order.is_custom) {
      openCustomOrderEdit(order);
      return;
    }
    setSelectedOrder(order);
    setStartInEdit(true);
    setDetailOpen(true);
  };

  // A custom order has no client account behind it, so it is corrected on the
  // same form it was booked on rather than the client-order edit panel.
  const openCustomOrderEdit = async (order) => {
    try {
      const full = await adminService.getCustomOrder(order.id);
      setCustomOrderEditing(full);
      setCustomOrderOpen(true);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not open that custom order');
    }
  };

  const handleCustomOrderSubmit = (payload, orderId) => (
    orderId
      ? adminService.updateCustomOrder(orderId, payload)
      : adminService.adminCreateCustomOrder(payload)
  );

  const customOrderProducts = useMemo(
    () => products
      .filter((p) => ['available', 'active'].includes(p.status))
      .map((p) => ({
        id: p.id, name: p.name, pack_size: p.pack_size, price: p.wholesale_price,
      })),
    [products]
  );

  // Batch operations
  const toggleSelectOrder = (orderId) => {
    setSelectedOrderIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) newSet.delete(orderId);
      else newSet.add(orderId);
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedOrderIds.size === filteredOrders.length) {
      setSelectedOrderIds(new Set());
    } else {
      setSelectedOrderIds(new Set(filteredOrders.map(o => o.id)));
    }
  };

  const handleBatchUpdate = async () => {
    if (!batchStatus || selectedOrderIds.size === 0) return;
    try {
      await adminService.batchUpdateOrderStatus(Array.from(selectedOrderIds), batchStatus);
      const updateBatch = o => selectedOrderIds.has(o.id) ? { ...o, status: batchStatus } : o;
      setOrders(prev => prev.map(updateBatch));
      setWeekFilteredOrders(prev => prev ? prev.map(updateBatch) : prev);
      toast.success(`Updated ${selectedOrderIds.size} orders to "${statusConfig[batchStatus].label}"`);
      setSelectedOrderIds(new Set());
      setBatchDialogOpen(false);
      setBatchStatus('');
    } catch (error) {
      console.error('Error batch updating:', error);
      toast.error('Failed to update orders');
    }
  };

  const baseOrders = weekFilteredOrders !== null ? weekFilteredOrders : orders;
  // The week strip hands over live work only. The status cards are a census of
  // the week rather than a total, so they count every status — and selecting
  // Cancelled or Failed Delivery brings those orders back into the table for
  // as long as that filter is on, otherwise both cards would be unclickable.
  const weekScope = weekScopeOrders !== null ? weekScopeOrders : orders;
  const tableOrders = isNonCountingStatus(statusFilter) ? weekScope : baseOrders;

  const availableProducts = useMemo(
    () => products.filter(p => p.status === 'available'),
    [products]
  );

  const filteredOrders = useMemo(() => tableOrders.filter(order => {
    const orderNumber = order.order_number || '';
    const clientName = order.client_name || '';
    const locationName = order.location_name || '';
    const needle = searchQuery.toLowerCase();
    const matchesSearch =
      orderNumber.toLowerCase().includes(needle) ||
      clientName.toLowerCase().includes(needle) ||
      locationName.toLowerCase().includes(needle);
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    const matchesClient = clientFilter === 'all' || order.client_id === clientFilter;
    return matchesSearch && matchesStatus && matchesClient;
  }), [tableOrders, searchQuery, statusFilter, clientFilter]);

  const statusCounts = useMemo(() => weekScope.reduce((acc, order) => {
    acc[order.status] = (acc[order.status] || 0) + 1;
    return acc;
  }, {}), [weekScope]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500"></div>
          <p className="mt-4 text-gray-600">Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-orders">
      <WeekView
        orders={orders}
        onFilterChange={(live, everyStatus) => {
          setWeekFilteredOrders(live);
          setWeekScopeOrders(live === null ? null : (everyStatus ?? live));
        }}
        showRevenueSummary={true}
      />

      {/* Client change requests on advance-invoiced orders (hidden when none) */}
      <ChangeRequestsSection onResolved={loadData} />

      {/* Order Status Workflow */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Order Status Workflow</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {Object.entries(statusConfig).map(([key, config]) => {
            const Icon = config.icon;
            return (
              <Card
                key={key}
                className={`cursor-pointer transition-all ${statusFilter === key ? 'ring-2 ring-amber-500' : ''}`}
                onClick={() => setStatusFilter(statusFilter === key ? 'all' : key)}
              >
                <CardContent className="p-3">
                  <div className="flex items-center gap-2">
                    <Icon className={`h-4 w-4 ${config.color.split(' ')[1]}`} />
                    <div>
                      <p className="text-lg font-bold">{statusCounts[key] || 0}</p>
                      <p className="text-xs text-gray-500">{config.label}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Filters & Batch Actions */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search orders or clients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {Object.entries(statusConfig).map(([key, config]) => (
              <SelectItem key={key} value={key}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={clientFilter} onValueChange={setClientFilter}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="All Clients" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Clients</SelectItem>
            {clients.map(client => (
              <SelectItem key={client.id} value={client.id}>{client.company_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {selectedOrderIds.size > 0 && (
          <Button
            className="bg-amber-500 hover:bg-amber-600"
            onClick={() => setBatchDialogOpen(true)}
          >
            Update {selectedOrderIds.size} Selected
          </Button>
        )}
        <Button variant="outline" onClick={() => setBacklogOpen(true)} data-testid="fix-order-locations-btn">
          <MapPin className="h-4 w-4 mr-2" />
          Missing Sites
        </Button>
        <Button variant="outline" onClick={() => setExportOpen(true)} data-testid="export-orders-btn">
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
        <Button
          variant="outline"
          onClick={() => { setCustomOrderEditing(null); setCustomOrderOpen(true); }}
          data-testid="new-custom-order-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Custom Order
        </Button>
        <Button className="bg-amber-600 hover:bg-amber-700" onClick={newOrder.handleOpenNewOrder} data-testid="new-order-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Order
        </Button>
      </div>

      <OrdersTable
        orders={filteredOrders}
        selectedOrderIds={selectedOrderIds}
        onToggleSelect={toggleSelectOrder}
        onToggleSelectAll={toggleSelectAll}
        onView={handleViewOrder}
        onReorder={newOrder.handleReorder}
        onEditRow={handleEditRow}
        onStatusChange={handleUpdateStatus}
        onReviewApproval={(order) => setApprovalOrderId(order.id)}
      />

      <BatchUpdateDialog
        open={batchDialogOpen}
        onOpenChange={setBatchDialogOpen}
        count={selectedOrderIds.size}
        batchStatus={batchStatus}
        setBatchStatus={setBatchStatus}
        onConfirm={handleBatchUpdate}
      />

      <OrderDetailDialog
        open={detailOpen}
        onOpenChange={(open) => { setDetailOpen(open); if (!open) setStartInEdit(false); }}
        order={selectedOrder}
        products={products}
        availableProducts={availableProducts}
        startInEdit={startInEdit}
        onUpdateStatus={handleUpdateStatus}
        onReorder={newOrder.handleReorder}
        onOrderSaved={handleOrderSaved}
        onEditCustom={openCustomOrderEdit}
      />

      <NewOrderDialog
        open={newOrder.newOrderOpen}
        onOpenChange={newOrder.setNewOrderOpen}
        clients={clients}
        newOrderClient={newOrder.newOrderClient}
        newOrderDate={newOrder.newOrderDate}
        newOrderTime={newOrder.newOrderTime}
        newOrderAddress={newOrder.newOrderAddress}
        newOrderPO={newOrder.newOrderPO}
        newOrderNotes={newOrder.newOrderNotes}
        newOrderItems={newOrder.newOrderItems}
        newOrderProducts={newOrder.newOrderProducts}
        newOrderTotal={newOrder.newOrderTotal}
        clientAddresses={newOrder.clientAddresses}
        loadingProducts={newOrder.loadingProducts}
        creatingOrder={newOrder.creatingOrder}
        onClientChange={newOrder.handleClientChange}
        setNewOrderDate={newOrder.setNewOrderDate}
        setNewOrderTime={newOrder.setNewOrderTime}
        setNewOrderAddress={newOrder.setNewOrderAddress}
        setNewOrderPO={newOrder.setNewOrderPO}
        setNewOrderNotes={newOrder.setNewOrderNotes}
        onAddItem={newOrder.handleNewOrderAddItem}
        onQtyChange={newOrder.handleNewOrderQtyChange}
        onPriceChange={newOrder.handleNewOrderPriceChange}
        onRemoveItem={newOrder.handleNewOrderRemoveItem}
        onCreateOrder={newOrder.handleCreateOrder}
        capMap={newOrder.capMap}
        overCapLines={newOrder.overCapLines}
        capGatingActive={newOrder.capGatingActive}
        capReasons={newOrder.capReasons}
        capReason={newOrder.capReason}
        setCapReason={newOrder.setCapReason}
        capNote={newOrder.capNote}
        setCapNote={newOrder.setCapNote}
        capReasonMissing={newOrder.capReasonMissing}
      />

      <CustomOrderFormDialog
        open={customOrderOpen}
        onOpenChange={(open) => { setCustomOrderOpen(open); if (!open) setCustomOrderEditing(null); }}
        order={customOrderEditing}
        products={customOrderProducts}
        onSubmit={handleCustomOrderSubmit}
        onSaved={loadData}
      />

      <LocationBacklogDialog
        open={backlogOpen}
        onOpenChange={setBacklogOpen}
        onApplied={loadData}
      />

      <ApprovalDialog
        orderId={approvalOrderId}
        open={!!approvalOrderId}
        onOpenChange={(v) => { if (!v) setApprovalOrderId(null); }}
        onResolved={() => { setApprovalOrderId(null); loadData(); }}
      />

      <ExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        entity="orders"
        title="Export Orders"
        description="Choose columns and date range. Active page filters will be honored."
        supportsDateRange={true}
        defaultPreset="30d"
        extraFilters={{
          ...(statusFilter !== 'all' ? { statuses: statusFilter } : {}),
          ...(clientFilter !== 'all' ? { client_id: clientFilter } : {}),
          ...(searchQuery ? { search: searchQuery } : {}),
        }}
        extraFilterSummary={[
          ...(statusFilter !== 'all' ? [{ label: 'Status', value: statusFilter }] : []),
          ...(clientFilter !== 'all'
            ? [{ label: 'Client', value: clients.find((c) => c.id === clientFilter)?.company_name || clientFilter }]
            : []),
          ...(searchQuery ? [{ label: 'Search', value: searchQuery }] : []),
        ]}
      />
    </div>
  );
};

// Wrap component with error boundary
const AdminOrders = () => (
  <OrdersErrorBoundary>
    <AdminOrdersContent />
  </OrdersErrorBoundary>
);

export default AdminOrders;
